import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, LogOut, Download, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import { api, adminAuth, API } from "@/lib/api";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";

function StatCard({ label, value, accent = "teal" }) {
    const colors = {
        teal: "border-l-weave-teal",
        red: "border-l-weave-red",
        yellow: "border-l-weave-yellow",
        ink: "border-l-weave-ink",
    };
    return (
        <div
            className={`bg-weave-surface border border-weave-border border-l-4 ${colors[accent]} p-6`}
            data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <p className="editorial-label text-weave-slate">{label}</p>
            <p className="text-4xl font-light text-weave-ink mt-3 tabular-nums">
                {value}
            </p>
        </div>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminDashboard() {
    const nav = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [reports, setReports] = useState([]);
    const [trials, setTrials] = useState([]);

    useEffect(() => {
        if (!adminAuth.getToken()) {
            nav("/admin/login");
            return;
        }
        (async () => {
            try {
                const [s, l, r, t] = await Promise.all([
                    api.get("/admin/stats"),
                    api.get("/admin/leads"),
                    api.get("/admin/reports"),
                    api.get("/admin/trials"),
                ]);
                setStats(s.data);
                setLeads(l.data);
                setReports(r.data);
                setTrials(t.data);
            } catch (err) {
                if (err?.response?.status === 401) {
                    adminAuth.clear();
                    nav("/admin/login");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [nav]);

    const logout = () => {
        adminAuth.clear();
        nav("/admin/login");
    };

    const downloadCsv = async (e, filename) => {
        e.preventDefault();
        const href = e.currentTarget.getAttribute("href");
        try {
            const resp = await fetch(href, {
                headers: { Authorization: `Bearer ${adminAuth.getToken()}` },
            });
            if (!resp.ok) throw new Error("Download failed");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            // fall-through: user sees nothing; we could show a toast, but keeping tight
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-weave-bg">
                <Loader2 className="w-8 h-8 animate-spin text-weave-teal" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-weave-bg">
            <Header minimal />
            <main
                className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16"
                data-testid="admin-dashboard"
            >
                <div className="flex items-end justify-between flex-wrap gap-4 border-b border-weave-ink pb-8">
                    <div>
                        <p className="editorial-label text-weave-teal mb-2">
                            Admin · Opportunity Unlock
                        </p>
                        <h1 className="text-4xl md:text-5xl font-light tracking-tight text-weave-ink">
                            Newsroom
                        </h1>
                        <p className="text-weave-slate mt-2">
                            What's coming through this week.
                        </p>
                    </div>
                    <button
                        onClick={logout}
                        className="btn-ghost"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign out
                    </button>
                </div>

                {/* STATS */}
                {stats && (
                    <>
                        <div className="grid md:grid-cols-5 gap-px bg-weave-border border border-weave-border mt-10">
                            <StatCard
                                label="Sessions"
                                value={stats.total_sessions}
                                accent="teal"
                            />
                            <StatCard
                                label="Completed"
                                value={stats.completed_sessions}
                                accent="teal"
                            />
                            <StatCard
                                label="Reports"
                                value={stats.total_reports}
                                accent="yellow"
                            />
                            <StatCard
                                label="Leads"
                                value={stats.total_leads}
                                accent="red"
                            />
                            <StatCard
                                label="1% Club"
                                value={stats.club_members}
                                accent="ink"
                            />
                        </div>
                        <div className="grid md:grid-cols-4 gap-px bg-weave-border border-x border-b border-weave-border">
                            <StatCard
                                label="Trials Offered"
                                value={stats.trials_offered || 0}
                                accent="teal"
                            />
                            <StatCard
                                label="Redeemed"
                                value={stats.trials_redeemed || 0}
                                accent="yellow"
                            />
                            <StatCard
                                label="Converted"
                                value={stats.trials_converted || 0}
                                accent="teal"
                            />
                            <StatCard
                                label="Exited"
                                value={stats.trials_exited || 0}
                                accent="red"
                            />
                        </div>
                    </>
                )}

                <Tabs defaultValue="reports" className="mt-14">
                    <div className="flex items-center justify-between flex-wrap gap-3 border-b border-weave-border">
                        <TabsList className="bg-transparent w-full md:w-auto justify-start rounded-none p-0">
                            <TabsTrigger
                                value="reports"
                                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-weave-teal data-[state=active]:shadow-none px-6 py-3 text-weave-slate data-[state=active]:text-weave-ink"
                                data-testid="tab-reports"
                            >
                                Reports ({reports.length})
                            </TabsTrigger>
                            <TabsTrigger
                                value="leads"
                                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-weave-teal data-[state=active]:shadow-none px-6 py-3 text-weave-slate data-[state=active]:text-weave-ink"
                                data-testid="tab-leads"
                            >
                                Leads ({leads.length})
                            </TabsTrigger>
                            <TabsTrigger
                                value="trials"
                                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-weave-red data-[state=active]:shadow-none px-6 py-3 text-weave-slate data-[state=active]:text-weave-ink"
                                data-testid="tab-trials"
                            >
                                Trials ({trials.length})
                            </TabsTrigger>
                        </TabsList>
                        <div className="hidden md:flex items-center gap-2 pb-2 pr-1">
                            <a
                                href={`${API}/admin/reports.csv`}
                                onClick={(e) => downloadCsv(e, "reports.csv")}
                                className="text-xs uppercase tracking-editorial text-weave-slate hover:text-weave-ink inline-flex items-center gap-1.5"
                                data-testid="csv-reports"
                            >
                                <Download className="w-3 h-3" />
                                Reports CSV
                            </a>
                            <span className="text-weave-muted">·</span>
                            <a
                                href={`${API}/admin/leads.csv`}
                                onClick={(e) => downloadCsv(e, "leads.csv")}
                                className="text-xs uppercase tracking-editorial text-weave-slate hover:text-weave-ink inline-flex items-center gap-1.5"
                                data-testid="csv-leads"
                            >
                                <Download className="w-3 h-3" />
                                Leads CSV
                            </a>
                            <span className="text-weave-muted">·</span>
                            <a
                                href={`${API}/admin/trials.csv`}
                                onClick={(e) => downloadCsv(e, "trials.csv")}
                                className="text-xs uppercase tracking-editorial text-weave-slate hover:text-weave-ink inline-flex items-center gap-1.5"
                                data-testid="csv-trials"
                            >
                                <Download className="w-3 h-3" />
                                Trials CSV
                            </a>
                            <span className="text-weave-muted">·</span>
                            <a
                                href={`${API}/admin/academy-interest.csv`}
                                onClick={(e) => downloadCsv(e, "academy-interest.csv")}
                                className="text-xs uppercase tracking-editorial text-weave-slate hover:text-weave-ink inline-flex items-center gap-1.5"
                                data-testid="csv-academy"
                            >
                                <Download className="w-3 h-3" />
                                Academy CSV
                            </a>
                        </div>
                    </div>

                    <TabsContent value="reports" className="mt-8">
                        <div className="border border-weave-border bg-weave-surface">
                            <Table data-testid="reports-table">
                                <TableHeader>
                                    <TableRow className="bg-weave-surface-alt hover:bg-weave-surface-alt">
                                        <TableHead className="editorial-label text-weave-slate">
                                            Business
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Headline
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Lead email
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Created
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Downloads
                                        </TableHead>
                                        <TableHead className="text-right editorial-label text-weave-slate">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {reports.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-center py-12 text-weave-muted"
                                            >
                                                No reports yet.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        reports.map((r) => (
                                            <TableRow key={r.id} className="hover:bg-weave-surface-alt">
                                                <TableCell className="font-medium text-weave-ink">
                                                    {r.business_name}
                                                </TableCell>
                                                <TableCell className="text-weave-slate max-w-xs truncate">
                                                    {r.headline}
                                                </TableCell>
                                                <TableCell className="text-weave-slate">
                                                    {r.email_captured || "—"}
                                                </TableCell>
                                                <TableCell className="text-weave-slate text-sm">
                                                    {fmtDate(r.created_at)}
                                                </TableCell>
                                                <TableCell className="text-weave-slate tabular-nums">
                                                    {r.download_count}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="inline-flex gap-3">
                                                        <Link
                                                            to={`/report/${r.id}`}
                                                            target="_blank"
                                                            className="text-weave-teal hover:underline text-sm inline-flex items-center gap-1"
                                                            data-testid={`view-report-${r.id}`}
                                                        >
                                                            View <ExternalLink className="w-3 h-3" />
                                                        </Link>
                                                        <a
                                                            href={`${API}/reports/${r.id}/pdf`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-weave-teal hover:underline text-sm inline-flex items-center gap-1"
                                                            data-testid={`download-report-${r.id}`}
                                                        >
                                                            PDF <Download className="w-3 h-3" />
                                                        </a>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="leads" className="mt-8">
                        <div className="border border-weave-border bg-weave-surface">
                            <Table data-testid="leads-table">
                                <TableHeader>
                                    <TableRow className="bg-weave-surface-alt hover:bg-weave-surface-alt">
                                        <TableHead className="editorial-label text-weave-slate">
                                            Email
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Business
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Club member
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Captured
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {leads.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                className="text-center py-12 text-weave-muted"
                                            >
                                                No leads yet.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        leads.map((l) => (
                                            <TableRow key={l.id} className="hover:bg-weave-surface-alt">
                                                <TableCell className="font-medium text-weave-ink">
                                                    {l.email}
                                                </TableCell>
                                                <TableCell className="text-weave-slate">
                                                    {l.business_name || "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {l.is_club_member ? (
                                                        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-editorial text-weave-teal">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-weave-teal" />
                                                            Yes
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs uppercase tracking-editorial text-weave-muted">
                                                            No
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-weave-slate text-sm">
                                                    {fmtDate(l.created_at)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="trials" className="mt-8">
                        <div className="border border-weave-border bg-weave-surface">
                            <Table data-testid="trials-table">
                                <TableHeader>
                                    <TableRow className="bg-weave-surface-alt hover:bg-weave-surface-alt">
                                        <TableHead className="editorial-label text-weave-slate">
                                            Email
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Business
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Code
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Status
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Offered
                                        </TableHead>
                                        <TableHead className="editorial-label text-weave-slate">
                                            Expires
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {trials.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-center py-12 text-weave-muted"
                                            >
                                                No trials offered yet.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        trials.map((t) => {
                                            const statusColour =
                                                t.status === "converted"
                                                    ? "text-weave-teal"
                                                    : t.status === "redeemed"
                                                    ? "text-[#a17320]"
                                                    : t.status?.startsWith(
                                                          "exited"
                                                      )
                                                    ? "text-weave-red"
                                                    : "text-weave-slate";
                                            return (
                                                <TableRow
                                                    key={t.id}
                                                    className="hover:bg-weave-surface-alt"
                                                >
                                                    <TableCell className="font-medium text-weave-ink">
                                                        {t.email}
                                                    </TableCell>
                                                    <TableCell className="text-weave-slate">
                                                        {t.business_name ||
                                                            "—"}
                                                    </TableCell>
                                                    <TableCell className="text-weave-ink font-mono text-sm">
                                                        {t.code}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span
                                                            className={`text-xs uppercase tracking-editorial ${statusColour}`}
                                                        >
                                                            {t.status}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-weave-slate text-sm">
                                                        {fmtDate(t.created_at)}
                                                    </TableCell>
                                                    <TableCell className="text-weave-slate text-sm">
                                                        {t.expires_at
                                                            ? new Date(
                                                                  t.expires_at *
                                                                      1000
                                                              ).toLocaleDateString(
                                                                  "en-GB",
                                                                  {
                                                                      day: "2-digit",
                                                                      month: "short",
                                                                  }
                                                              )
                                                            : "—"}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
